// Package imports
import axios from 'axios';
import Cookies from 'universal-cookie';

// Special imports
import { config } from '../config';

// Type imports
import { SignupLoginInput } from '../types/authentication/SignupLoginInput';
import { AuthResult } from '../types/authentication/AuthResult';
import { ReauthResult } from '../types/authentication/ReauthResult';
import { LoginResultType } from '../types/authentication/LoginResultType';
import { LoginReturn } from '../types/authentication/LoginReturn';
import { SignupResultType } from '../types/authentication/SignupResultType';
import { UserRequestsResult } from '../types/user/UserRequestsResult';
import { AdminUsersResult } from '../types/user/AdminUsersResult';
import { EmailTemplateId } from '../types/emailTemplates/EmailTemplateId';
import { EmailTemplate } from '../types/emailTemplates/EmailTemplate';
import { EmailTemplatesResult } from '../types/emailTemplates/EmailTemplatesResult';
import { BlogLinkResult } from '../types/blog/BlogLinkResult';
import { BlogPost } from '../types/blog/BlogPost';
import { BlogPostsResult } from '../types/blog/BlogPostsResult';
import { NewsletterEmailId } from '../types/newsletter/NewsletterEmailId';
import { NewsletterEmail } from '../types/newsletter/NewsletterEmail';
import { NewsletterEmailsResult } from '../types/newsletter/NewsletterEmailsResult';
import { NewsletterUsersResult } from '../types/newsletter/NewsletterUsersResult';

const baseDomain =
  config.environment === 'development'
    ? 'http://localhost:8080'
    : `${config.serverDomain}`;

export const ApiService = {
  // =====================================================================
  // ***** Authentication *****
  // =====================================================================

  async signup(user: SignupLoginInput): Promise<SignupResultType> {
    try {
      const result = await axios.post(baseDomain + '/admin/signup', user);

      const data: boolean = result.data;

      if (data) {
        return SignupResultType.OK;
      }

      return SignupResultType.ERROR;
    } catch (error: any) {
      try {
        if (error.response.status === 409) {
          return SignupResultType.DUPLICATE;
        }
      } catch (err) {}

      return SignupResultType.ERROR;
    }
  },

  async login(user: SignupLoginInput) {
    try {
      const result = await axios.post(baseDomain + '/admin/login', user);

      const data: AuthResult = result.data;

      const cookies = new Cookies();

      if (data.refreshToken) {
        cookies.set('refreshToken', data.refreshToken, {
          path: '/',
          expires: new Date(Date.now() + 60 * 60 * 24 * 365 * 1000),
        });
      }
      if (data.accessToken) {
        axios.defaults.headers.common['Authorization'] =
          'Bearer ' + data.accessToken;
      }

      const returnObj: LoginReturn = {
        resultType: LoginResultType.OK,
        user: data.user,
      };

      return returnObj;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const returnObj: LoginReturn = {
            resultType: LoginResultType.INVALID,
            user: null,
          };

          return returnObj;
        }
      } catch (err) {}

      const returnObj: LoginReturn = {
        resultType: LoginResultType.ERROR,
        user: null,
      };

      return returnObj;
    }
  },

  async initialAuthenticate(currentRefreshToken: string) {
    try {
      axios.defaults.headers.common['Authorization'] =
        'Bearer ' + currentRefreshToken;
      const result = await axios.post(
        baseDomain + '/admin/initial-authenticate'
      );

      const data: AuthResult = result.data;

      const cookies = new Cookies();

      if (data.refreshToken) {
        cookies.set('refreshToken', data.refreshToken, {
          path: '/',
          expires: new Date(Date.now() + 60 * 60 * 24 * 365 * 1000),
        });
      }
      if (data.accessToken) {
        axios.defaults.headers.common['Authorization'] =
          'Bearer ' + data.accessToken;
      }

      return data.user;
    } catch (error) {
      const cookies = new Cookies();
      cookies.remove('refreshToken', { path: '/' });
      axios.defaults.headers.common['Authorization'] = '';
      return null;
    }
  },

  async reauthenticate() {
    try {
      const cookies = new Cookies();
      const current_refresh_token = cookies.get('refreshToken');
      axios.defaults.headers.common['Authorization'] =
        'Bearer ' + current_refresh_token;

      const result = await axios.post(baseDomain + '/admin/reauthenticate');

      const data: ReauthResult = result.data;

      if (data.refreshToken) {
        cookies.set('refreshToken', data.refreshToken, {
          path: '/',
          expires: new Date(Date.now() + 60 * 60 * 24 * 365 * 1000),
        });
      }
      if (data.accessToken) {
        axios.defaults.headers.common['Authorization'] =
          'Bearer ' + data.accessToken;
      }

      return true;
    } catch (error) {
      console.log(error);
      const cookies = new Cookies();
      cookies.remove('refreshToken', { path: '/' });
      axios.defaults.headers.common['Authorization'] = '';
      return false;
    }
  },

  async logout() {
    try {
      const cookies = new Cookies();
      const current_refresh_token = cookies.get('refreshToken');
      axios.defaults.headers.common['Authorization'] =
        'Bearer ' + current_refresh_token;

      await axios.delete(baseDomain + '/admin/logout');

      cookies.remove('refreshToken', { path: '/' });
      axios.defaults.headers.common['Authorization'] = '';

      return true;
    } catch (error) {
      console.log(error);
      const cookies = new Cookies();
      cookies.remove('refreshToken', { path: '/' });
      axios.defaults.headers.common['Authorization'] = '';
      return true;
    }
  },

  async googleAuthRequest(authType: string) {
    return baseDomain + '/google/auth/request?client=web&auth=' + authType;
    // try {
    //   const result = await axios.get(baseDomain + '/admin/google/auth/request');

    //   const data: GoogleAuthRequestResult = result.data;

    //   return data;
    // } catch (error) {
    //   console.log(error);
    //   return null;
    // }
  },

  async googleAuth(authType: string, code: string) {
    try {
      const result = await axios.post(
        baseDomain + '/admin/google/auth?client=web&auth=' + authType,
        {
          code: code,
        }
      );

      const data: AuthResult | boolean = result.data;

      if (typeof data !== 'boolean') {
        const cookies = new Cookies();

        if (data.refreshToken) {
          cookies.set('refreshToken', data.refreshToken, {
            path: '/',
            expires: new Date(Date.now() + 60 * 60 * 24 * 365 * 1000),
          });
        }
        if (data.accessToken) {
          axios.defaults.headers.common['Authorization'] =
            'Bearer ' + data.accessToken;
        }
        return data.user;
      } else {
        return data as boolean;
      }
    } catch (error) {
      const cookies = new Cookies();
      cookies.remove('refreshToken', { path: '/' });
      axios.defaults.headers.common['Authorization'] = '';
      return null;
    }
  },

  // =====================================================================
  // ***** User *****
  // =====================================================================
  async getUserRequests(
    page: number,
    pageAmount: number
  ): Promise<UserRequestsResult | null> {
    try {
      const result = await axios.get(
        baseDomain +
          '/admin/users/request?page=' +
          page +
          '&pageAmount=' +
          pageAmount
      );

      const data: UserRequestsResult = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.getUserRequests(page, pageAmount);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async declineRequestUser(id: string): Promise<boolean> {
    try {
      const result = await axios.delete(
        baseDomain + '/admin/users/' + id + '/request'
      );

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.declineRequestUser(id);
          }
        }
      } catch (err) {}

      return false;
    }
  },

  async acceptRequestUser(id: string, role: string): Promise<boolean> {
    try {
      const input = {
        id: id,
        role: role,
      };

      const result = await axios.post(
        baseDomain + '/admin/users/request',
        input
      );

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.acceptRequestUser(id, role);
          }
        }
      } catch (err) {}

      return false;
    }
  },

  async getAdminUsers(
    page: number,
    pageAmount: number
  ): Promise<AdminUsersResult | null> {
    try {
      const result = await axios.get(
        baseDomain + '/admin/users?page=' + page + '&pageAmount=' + pageAmount
      );

      const data: AdminUsersResult = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.getAdminUsers(page, pageAmount);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async deleteAdminUser(id: string): Promise<boolean> {
    try {
      const result = await axios.delete(baseDomain + '/admin/users/' + id);

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.deleteAdminUser(id);
          }
        }
      } catch (err) {}

      return false;
    }
  },

  async updateAdminUser(id: string, role: string): Promise<boolean> {
    try {
      const input = {
        id: id,
        role: role,
      };

      const result = await axios.put(baseDomain + '/admin/users', input);

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.updateAdminUser(id, role);
          }
        }
      } catch (err) {}

      return false;
    }
  },

  // =====================================================================
  // ***** Email Templates *****
  // =====================================================================
  async createEmailTemplate(
    name: string,
    html: string,
    design: any
  ): Promise<string | null> {
    try {
      const input = {
        name: name,
        html: html,
        design: design,
      };

      const result = await axios.post(
        baseDomain + '/admin/email-templates',
        input
      );

      const data: EmailTemplateId = result.data;

      return data.id;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.createEmailTemplate(name, html, design);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async updateEmailTemplate(
    id: string,
    name: string,
    html: string,
    design: any
  ): Promise<boolean> {
    try {
      const input = {
        id: id,
        name: name,
        html: html,
        design: design,
      };

      const result = await axios.put(
        baseDomain + '/admin/email-templates',
        input
      );

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.updateEmailTemplate(id, name, html, design);
          }
        }
      } catch (err) {}

      return false;
    }
  },

  async getEmailTemplate(id: string): Promise<EmailTemplate | null> {
    try {
      const result = await axios.get(
        baseDomain + '/admin/email-templates/' + id
      );

      const data: EmailTemplate = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.getEmailTemplate(id);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async duplicateEmailTemplate(id: string): Promise<EmailTemplate | null> {
    try {
      const result = await axios.post(
        baseDomain + '/admin/email-templates/' + id + '/duplicate'
      );

      const data: EmailTemplate = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.duplicateEmailTemplate(id);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async deleteEmailTemplate(id: string): Promise<boolean> {
    try {
      const result = await axios.delete(
        baseDomain + '/admin/email-templates/' + id
      );

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.deleteEmailTemplate(id);
          }
        }
      } catch (err) {}

      return false;
    }
  },

  async getEmailTemplates(
    search: string,
    page: number,
    pageAmount: number
  ): Promise<EmailTemplatesResult | null> {
    try {
      const result = await axios.get(
        baseDomain +
          '/admin/email-templates?search=' +
          encodeURI(search) +
          '&page=' +
          page +
          '&pageAmount=' +
          pageAmount
      );

      const data: EmailTemplatesResult = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.getEmailTemplates(search, page, pageAmount);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  // =====================================================================
  // ***** Newsletter *****
  // =====================================================================
  async createNewsletterEmail(
    name: string,
    subject: string,
    html: string,
    design: any
  ): Promise<string | null> {
    try {
      const input = {
        name: name,
        subject: subject,
        html: html,
        design: design,
      };

      const result = await axios.post(
        baseDomain + '/admin/newsletter-emails',
        input
      );

      const data: NewsletterEmailId = result.data;

      return data.id;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.createNewsletterEmail(
              name,
              subject,
              html,
              design
            );
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async updateNewsletterEmail(
    id: string,
    name: string,
    subject: string,
    html: string,
    design: any
  ): Promise<boolean> {
    try {
      const input = {
        id: id,
        name: name,
        subject: subject,
        html: html,
        design: design,
      };

      const result = await axios.put(
        baseDomain + '/admin/newsletter-emails',
        input
      );

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.updateNewsletterEmail(
              id,
              name,
              subject,
              html,
              design
            );
          }
        }
      } catch (err) {}

      return false;
    }
  },

  async getNewsletterEmail(id: string): Promise<NewsletterEmail | null> {
    try {
      const result = await axios.get(
        baseDomain + '/admin/newsletter-emails/' + id
      );

      const data: NewsletterEmail = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.getNewsletterEmail(id);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async duplicateNewsletterEmail(id: string): Promise<NewsletterEmail | null> {
    try {
      const result = await axios.post(
        baseDomain + '/admin/newsletter-emails/' + id + '/duplicate'
      );

      const data: NewsletterEmail = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.duplicateNewsletterEmail(id);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async sendNewsletterEmail(id: string): Promise<boolean> {
    try {
      const result = await axios.post(
        baseDomain + '/admin/newsletter-emails/' + id
      );

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.sendNewsletterEmail(id);
          }
        }
      } catch (err) {}

      return false;
    }
  },

  async deleteNewsletterEmail(id: string): Promise<boolean> {
    try {
      const result = await axios.delete(
        baseDomain + '/admin/newsletter-emails/' + id
      );

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.deleteNewsletterEmail(id);
          }
        }
      } catch (err) {}

      return false;
    }
  },

  async getNewsletterEmails(
    search: string,
    page: number,
    pageAmount: number
  ): Promise<NewsletterEmailsResult | null> {
    try {
      const result = await axios.get(
        baseDomain +
          '/admin/newsletter-emails?search=' +
          encodeURI(search) +
          '&page=' +
          page +
          '&pageAmount=' +
          pageAmount
      );

      const data: NewsletterEmailsResult = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.getNewsletterEmails(search, page, pageAmount);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async getNewsletterUsers(
    search: string,
    page: number,
    pageAmount: number
  ): Promise<NewsletterUsersResult | null> {
    try {
      const result = await axios.get(
        baseDomain +
          '/admin/newsletter?search=' +
          encodeURI(search) +
          '&page=' +
          page +
          '&pageAmount=' +
          pageAmount
      );

      const data: NewsletterUsersResult = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.getNewsletterUsers(search, page, pageAmount);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async unsubscribeNewsletter(id: string): Promise<boolean> {
    try {
      const result = await axios.delete(baseDomain + '/newsletter/' + id);

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.unsubscribeNewsletter(id);
          }
        }
      } catch (err) {}

      return false;
    }
  },

  // =====================================================================
  // ***** Blog *****
  // =====================================================================
  async createBlogPost(
    title: string,
    content: string
  ): Promise<BlogLinkResult | null> {
    try {
      const input = {
        title: title,
        content: content,
      };

      const result = await axios.post(baseDomain + '/admin/blogs', input);

      const data: BlogLinkResult = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.createBlogPost(title, content);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async updateBlogPost(
    id: string,
    title: string,
    content: string
  ): Promise<BlogLinkResult | null> {
    try {
      const input = {
        id: id,
        title: title,
        content: content,
      };

      const result = await axios.put(baseDomain + '/admin/blogs', input);

      const data: BlogLinkResult = result.data;

      return data;
    } catch (error: any) {
      console.log(error);
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.updateBlogPost(id, title, content);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async getBlogPost(id: string): Promise<BlogPost | null> {
    try {
      const result = await axios.get(baseDomain + '/blogs/' + id);

      const data: BlogPost = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.getBlogPost(id);
          }
        }
      } catch (err) {}

      return null;
    }
  },

  async deleteBlogPost(id: string): Promise<boolean> {
    try {
      const result = await axios.delete(baseDomain + '/admin/blogs/' + id);

      const data: boolean = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.deleteBlogPost(id);
          }
        }
      } catch (err) {}

      return false;
    }
  },

  async getBlogPosts(
    search: string,
    page: number,
    pageAmount: number
  ): Promise<BlogPostsResult | null> {
    try {
      const result = await axios.get(
        baseDomain +
          '/blogs?search=' +
          encodeURI(search) +
          '&page=' +
          page +
          '&pageAmount=' +
          pageAmount
      );

      const data: BlogPostsResult = result.data;

      return data;
    } catch (error: any) {
      try {
        if (error.response.status === 401) {
          const auth: boolean = await this.reauthenticate();
          if (auth) {
            return await this.getBlogPosts(search, page, pageAmount);
          }
        }
      } catch (err) {}

      return null;
    }
  },
};
